import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, UserPlus, Check, X, Swords, Loader2, UserMinus, RotateCcw, Trophy } from "lucide-react";
import WagerPicker, { GAMES, createChallenge } from "../components/WagerPicker";
import { Toaster } from "../components/ui/toaster";
import { toast } from "../hooks/use-toast";
import { auth, friendsApi } from "../lib/api";

const Avatar = ({ name }) => (
  <span className="w-10 h-10 rounded-full bg-[#334155] flex items-center justify-center font-display font-bold text-lg uppercase shrink-0">{name[0]}</span>
);

const H2H = ({ h, name }) => {
  const played = h.w + h.l + h.d;
  if (!played) return <span className="text-slate-500">No games yet</span>;
  const tone = h.w > h.l ? "text-[#10B981]" : h.l > h.w ? "text-[#EF4444]" : "text-slate-300";
  return (
    <span data-testid={`h2h-${name}`} className={`font-semibold ${tone}`}>
      You {h.w}–{h.l} {name}{h.d ? ` · ${h.d} draw${h.d > 1 ? "s" : ""}` : ""}
    </span>
  );
};

const Row = ({ u, children, testid, sub }) => (
  <div data-testid={testid} className="flex items-center gap-3 rounded-lg border border-[#334155] bg-[#1E293B] px-3 py-2.5">
    <Avatar name={u.username} />
    <div className="flex-1 min-w-0">
      <div className="font-semibold truncate">{u.username}</div>
      <div className="text-[11px] text-slate-400 truncate">{sub || `${u.pep_wins} Pep wins · ${u.shootout_wins} shootout wins`}</div>
    </div>
    {children}
  </div>
);

const Leaderboard = ({ rows }) => (
  <div data-testid="friend-leaderboard" className="rounded-lg border border-[#334155] bg-[#1E293B] divide-y divide-[#334155]">
    {rows.map((r, i) => (
      <div key={r.username} data-testid={`leaderboard-row-${r.username}`} className={`flex items-center gap-3 px-3 py-2.5 ${r.me ? "bg-[#10B981]/[0.06]" : ""}`}>
        <span className="w-6 text-center font-display font-bold text-lg text-slate-400 tabular">{i + 1}</span>
        <span className="flex-1 min-w-0 flex items-center gap-2">
          <span className="font-semibold truncate">{r.me ? `${r.username} (you)` : r.username}</span>
          {r.top && (
            <span data-testid="top-mate-badge" className="flex items-center gap-1 rounded bg-[#F59E0B]/15 text-[#F59E0B] text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 shrink-0">
              <Trophy className="w-3 h-3" /> Top mate
            </span>
          )}
        </span>
        <span className="text-[11px] text-slate-500 shrink-0">{r.games} game{r.games === 1 ? "" : "s"}</span>
        <span className={`w-16 text-right font-display font-bold text-lg tabular ${r.net > 0 ? "text-[#10B981]" : r.net < 0 ? "text-[#EF4444]" : "text-slate-300"}`}>
          {r.net < 0 ? "-" : r.net > 0 ? "+" : ""}£{Math.abs(r.net)}
        </span>
      </div>
    ))}
  </div>
);

const SectionTitle = ({ children }) => (
  <div className="flex items-center gap-3 mt-8 mb-2.5">
    <h2 className="font-display font-bold uppercase text-lg tracking-wide">{children}</h2>
    <span className="h-px flex-1 bg-[#334155]" />
  </div>
);

const errMsg = (e) => e?.response?.data?.detail || "Something went wrong, try again";

const AddFriend = ({ onAdded }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return undefined; }
    timer.current = setTimeout(() => friendsApi.search(q.trim()).then(setResults).catch(() => setResults([])), 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const add = async (name) => {
    setBusy(name);
    try {
      const f = await friendsApi.add(name);
      toast({ title: `${f.username} added`, description: f.mutual ? "You're now friends." : "They'll get a request to add you back." });
      setQ("");
      onAdded();
    } catch (e) {
      toast({ title: "Couldn't add friend", description: errMsg(e), variant: "destructive" });
    }
    setBusy(null);
  };

  const isEmail = q.includes("@");
  return (
    <div>
      <form onSubmit={(e) => { e.preventDefault(); if (q.trim()) add(q.trim()); }} className="flex gap-2">
        <label className="flex-1 flex items-center gap-2 rounded-lg border border-[#334155] bg-[#1E293B] px-3 focus-within:border-[#10B981]">
          <Search className="w-4 h-4 text-slate-400" />
          <input data-testid="friend-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Username or email"
            className="flex-1 bg-transparent py-3 text-sm outline-none placeholder:text-slate-500" autoComplete="off" />
        </label>
        <button data-testid="friend-add-btn" type="submit" disabled={!q.trim() || !!busy} className="btn-green rounded-lg px-4 font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50">
          <UserPlus className="w-4 h-4" /> Add
        </button>
      </form>
      {results.length > 0 && (
        <div data-testid="friend-suggestions" className="mt-2 space-y-1.5">
          {results.map((u) => (
            <Row key={u.username} u={u} testid={`friend-suggestion-${u.username}`}>
              {u.is_friend ? (
                <span className="text-xs text-slate-400 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Added</span>
              ) : (
                <button data-testid={`friend-suggestion-add-${u.username}`} onClick={() => add(u.username)} disabled={busy === u.username}
                  className="rounded-md bg-[#10B981] text-[#0F172A] px-3 py-1.5 text-xs font-bold flex items-center gap-1">
                  {busy === u.username ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />} Add
                </button>
              )}
            </Row>
          ))}
        </div>
      )}
      {q.trim().length >= 2 && !results.length && (
        <p data-testid="friend-search-empty" className="text-xs text-slate-500 mt-2">{isEmail ? "Tap Add to find them by exact email." : "No matching usernames yet."}</p>
      )}
    </div>
  );
};

export default function Friends() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [board, setBoard] = useState([]);
  const [again, setAgain] = useState(null);

  const load = () => {
    friendsApi.list().then(setData).catch(() => setData({ friends: [], requests: [] }));
    friendsApi.leaderboard().then(setBoard).catch(() => setBoard([]));
  };
  const challengeAgain = async (u) => {
    const game = GAMES.find((g) => g.id === (u.h2h.last.kind === "draft" ? "draft" : "penalties"));
    setAgain(u.username);
    try {
      const { to } = await createChallenge(game, u.h2h.last.stake, u.username);
      toast({ title: `Challenge sent to ${u.username}`, description: `${game.title}${u.h2h.last.stake ? ` · £${u.h2h.last.stake}` : " · Friendly"}` });
      navigate(to);
    } catch (e) {
      toast({ title: "Couldn't send challenge", description: errMsg(e), variant: "destructive" });
    }
    setAgain(null);
  };
  useEffect(() => { auth.ensure().then(setUser); load(); }, []);

  const accept = async (name) => {
    try { await friendsApi.add(name); toast({ title: `You're now friends with ${name}` }); load(); } catch (e) { toast({ title: "Couldn't accept", description: errMsg(e), variant: "destructive" }); }
  };
  const decline = async (name) => { await friendsApi.decline(name).catch(() => {}); load(); };
  const remove = async (name) => { await friendsApi.remove(name).catch(() => {}); load(); };

  return (
    <div className="bg-app min-h-screen text-white">
      <header className="sticky top-0 z-30 bg-[#0F172A]/95 backdrop-blur border-b border-[#334155]">
        <div className="max-w-md mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="friends-back-btn" onClick={() => navigate("/")} className="pill-nav rounded-full px-3.5 py-1.5 flex items-center gap-2 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
          <h1 className="font-display font-extrabold uppercase text-xl tracking-wide">Friends</h1>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 pt-6 pb-12">
        <p className="text-slate-400 text-sm mb-3">Add mates by username or email, then challenge them in one tap.</p>
        <AddFriend onAdded={load} />

        {!data ? (
          <div className="flex justify-center py-10 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <>
            {data.requests.length > 0 && (
              <>
                <SectionTitle>Requests</SectionTitle>
                <div className="space-y-2" data-testid="friend-requests">
                  {data.requests.map((u) => (
                    <Row key={u.username} u={u} testid={`friend-request-${u.username}`}>
                      <button data-testid={`friend-accept-${u.username}`} onClick={() => accept(u.username)} className="rounded-md bg-[#10B981] text-[#0F172A] p-2" title="Accept"><Check className="w-4 h-4" /></button>
                      <button data-testid={`friend-decline-${u.username}`} onClick={() => decline(u.username)} className="rounded-md bg-[#334155] text-slate-200 p-2" title="Decline"><X className="w-4 h-4" /></button>
                    </Row>
                  ))}
                </div>
              </>
            )}

            {board.length > 1 && (
              <>
                <SectionTitle>This week</SectionTitle>
                <p className="text-[11px] text-slate-500 -mt-1 mb-2">Net virtual £ from wagers since Monday</p>
                <Leaderboard rows={board} />
              </>
            )}

            <SectionTitle>Your friends{data.friends.length ? ` (${data.friends.length})` : ""}</SectionTitle>
            {data.friends.length === 0 ? (
              <p data-testid="friends-empty" className="text-sm text-slate-500">No friends yet. Search for a mate above.</p>
            ) : (
              <div className="space-y-2" data-testid="friends-list">
                {data.friends.map((u) => (
                  <div key={u.username}>
                  <Row u={u} testid={`friend-${u.username}`} sub={<H2H h={u.h2h} name={u.username} />}>
                    <button data-testid={`friend-remove-${u.username}`} onClick={() => remove(u.username)} className="text-slate-500 hover:text-[#EF4444] p-1.5 transition-colors" title="Remove friend"><UserMinus className="w-4 h-4" /></button>
                    <button data-testid={`friend-challenge-${u.username}`} onClick={() => setChallenge(u.username)} className="btn-green rounded-md px-3 py-2 text-xs font-bold flex items-center gap-1.5">
                      <Swords className="w-3.5 h-3.5" /> Challenge
                    </button>
                  </Row>
                  {u.h2h.last && (
                    <button data-testid={`friend-again-${u.username}`} onClick={() => challengeAgain(u)} disabled={again === u.username}
                      className="mt-1 ml-[52px] text-xs font-semibold text-[#38BDF8] hover:text-white flex items-center gap-1.5 disabled:opacity-50 transition-colors">
                      {again === u.username ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                      Challenge again · {u.h2h.last.kind === "draft" ? "Draft XI" : "Penalties"} {u.h2h.last.stake ? `£${u.h2h.last.stake}` : "friendly"}
                    </button>
                  )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
      <WagerPicker open={!!challenge} friend={challenge} user={user} onClose={() => setChallenge(null)} onSignup={() => {}} />
      <Toaster />
    </div>
  );
}
